import { VStack, HStack, Text, Heading, Grid, Card, Button, FAQAccordion, useIsMobile, useIsTablet, Animate } from 'wss3-forge'
import {
  ChevronRight20Regular,
  Open20Regular,
  Bot24Regular,
  PersonSupport24Regular,
  Clock24Regular,
  Checkmark20Regular,
  PeopleCommunity24Regular,
  Book24Regular
} from '@fluentui/react-icons'

const DISCORD_LINK = 'https://discord.gg/ZRABGm7we8'
const SHOP_LINK = 'https://murphy-workshop.com'

const faqItems = [
  {
    question: 'How do I find documentation for my plugin?',
    answer: 'Use the Documentation dropdown in the navigation bar to select your plugin, or use the search function (Ctrl+K) to find specific topics across all documentation.'
  },
  {
    question: 'The documentation doesn\'t answer my question. What now?',
    answer: 'Join our Discord server and ask Murphy\'s AI Support. It has access to all documentation and can provide personalized guidance. If needed, a human developer will jump in.'
  },
  {
    question: 'Which frameworks are supported?',
    answer: 'We support multiple frameworks including VORP, RSG, RedEM:RP, and standalone solutions. Check each plugin\'s documentation for specific compatibility information.'
  },
  {
    question: 'How do I report a documentation issue?',
    answer: 'If you find outdated or incorrect information, please report it on Discord. Our team reviews all feedback and keeps the docs up to date.'
  },
  {
    question: 'Can I contribute to the documentation?',
    answer: 'We welcome community contributions! Share your tips, configs, and examples on Discord. The best contributions get added to the official docs.'
  },
  {
    question: 'Is there a test server to try plugins?',
    answer: 'Yes! Visit the Test Server page on our main website for connection details. Try all plugins before you buy.'
  }
]

export function SupportPage() {
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()

  const handleOpenDiscord = () => {
    window.open(DISCORD_LINK, '_blank')
  }

  const handleOpenShop = () => {
    window.open(SHOP_LINK, '_blank')
  }

  return (
    <VStack gap="none">
      {/* Hero Section */}
      <section style={{ padding: isMobile ? '100px 0 60px 0' : '120px 0 80px 0', backgroundColor: '#0F0F0F' }}>
        <div className="page-content">
          <Animate type="fadeIn">
            {isMobile ? (
              <VStack gap="lg">
                <VStack gap="md">
                  <Text
                    size="sm"
                    style={{
                      color: 'var(--brand-primary)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '2px'
                    }}
                  >
                    Support
                  </Text>
                  <Heading
                    level={1}
                    style={{
                      color: '#FFFFFF',
                      fontSize: '32px',
                      fontWeight: 700,
                      lineHeight: 1.2
                    }}
                  >
                    Get Help in Minutes,<br />
                    <span style={{ color: 'var(--brand-primary)' }}>Not Days</span>
                  </Heading>
                  <Text style={{ color: '#CACACA', fontSize: '14px' }}>
                    Our AI assistant resolves most issues instantly on Discord. When you need a human, our team is right there.
                  </Text>
                  <Button
                    icon={<Open20Regular />}
                    onClick={handleOpenDiscord}
                    style={{ background: 'linear-gradient(155deg, #f6a00e 0%, #8B5A00 100%)', alignSelf: 'flex-start' }}
                  >
                    Join Our Discord
                  </Button>
                </VStack>

                <div style={{ width: '100%', overflow: 'hidden' }}>
                  <img
                    src="/visu_become_part.png"
                    alt="Murphy Discord Support"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: '8px'
                    }}
                  />
                </div>
              </VStack>
            ) : (
              <HStack
                gap={isTablet ? 'lg' : 'xl'}
                style={{
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <VStack gap="md" style={{
                  flex: 1,
                  minWidth: '280px',
                  maxWidth: isTablet ? '400px' : '500px'
                }}>
                  <Text
                    size="sm"
                    style={{
                      color: 'var(--brand-primary)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '2px'
                    }}
                  >
                    Support
                  </Text>
                  <Heading
                    level={1}
                    style={{
                      color: '#FFFFFF',
                      fontSize: isTablet ? '36px' : '44px',
                      fontWeight: 700,
                      lineHeight: 1.1
                    }}
                  >
                    Get Help in Minutes,<br />
                    <span style={{ color: 'var(--brand-primary)' }}>Not Days</span>
                  </Heading>
                  <Text style={{ color: '#CACACA', fontSize: '16px', maxWidth: '400px' }}>
                    Our AI assistant resolves most issues instantly on Discord. When you need a human, our team is right there.
                  </Text>
                  <Button
                    icon={<Open20Regular />}
                    onClick={handleOpenDiscord}
                    style={{ background: 'linear-gradient(155deg, #f6a00e 0%, #8B5A00 100%)' }}
                  >
                    Join Our Discord
                  </Button>
                </VStack>

                <div
                  style={{
                    flex: 1,
                    minWidth: '280px',
                    maxWidth: isTablet ? '400px' : '500px',
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src="/visu_become_part.png"
                    alt="Murphy Discord Support"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
              </HStack>
            )}
          </Animate>
        </div>
      </section>

      {/* Divider */}
      <div style={{ width: '100%', height: '1.5px', backgroundColor: '#1A1A1A' }} />

      {/* How It Works Section - Two Column Layout */}
      <section style={{ backgroundColor: '#0F0F0F' }}>
        <div className="page-content">
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%'
            }}
          >
            {!isMobile && <div style={{ width: '1.5px', backgroundColor: '#1A1A1A' }} />}

            {/* Left Column - AI Support */}
            <VStack gap="sm" style={{
              flex: 1,
              padding: isMobile ? '32px 16px' : isTablet ? '40px 24px' : '48px 40px',
              justifyContent: 'space-between'
            }}>
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Bot24Regular style={{ color: 'var(--brand-primary)', width: 28, height: 28 }} />
                  <Heading level={3} style={{ color: '#FFFFFF', fontSize: isMobile ? '18px' : '20px' }}>
                    Murphy's AI Support
                  </Heading>
                </HStack>
                <Text style={{ color: '#CACACA', fontSize: isMobile ? '14px' : '15px', lineHeight: 1.6 }}>
                  Describe your issue and get instant answers, code snippets, and troubleshooting steps. Available 24/7, no waiting.
                </Text>
              </VStack>
              <Text style={{ color: '#848484', fontSize: '13px', marginTop: isMobile ? '16px' : '24px' }}>
                Responds in seconds
              </Text>
            </VStack>

            <div style={{
              width: isMobile ? '100%' : '1.5px',
              height: isMobile ? '1.5px' : 'auto',
              backgroundColor: '#1A1A1A'
            }} />

            {/* Right Column - Human Support */}
            <VStack gap="sm" style={{
              flex: 1,
              padding: isMobile ? '32px 16px' : isTablet ? '40px 24px' : '48px 40px',
              justifyContent: 'space-between'
            }}>
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <PersonSupport24Regular style={{ color: 'var(--brand-primary)', width: 28, height: 28 }} />
                  <Heading level={3} style={{ color: '#FFFFFF', fontSize: isMobile ? '18px' : '20px' }}>
                    Human Backup
                  </Heading>
                </HStack>
                <Text style={{ color: '#CACACA', fontSize: isMobile ? '14px' : '15px', lineHeight: 1.6 }}>
                  Complex problem? A real developer jumps in to help you directly. We don't hide behind bots when you need real help.
                </Text>
              </VStack>
              <Text style={{ color: '#848484', fontSize: '13px', marginTop: isMobile ? '16px' : '24px' }}>
                Within 24 hours on business days
              </Text>
            </VStack>

            {!isMobile && <div style={{ width: '1.5px', backgroundColor: '#1A1A1A' }} />}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div style={{ width: '100%', height: '1.5px', backgroundColor: '#1A1A1A' }} />

      {/* Commitments Section */}
      <section style={{ padding: isMobile ? '40px 0' : '60px 0', backgroundColor: '#161616' }}>
        <div className="page-content">
          <Animate type="slideInUp" delay={50}>
            <VStack gap="lg">
              <VStack gap="sm">
                <Heading
                  level={2}
                  style={{
                    color: '#FFFFFF',
                    fontSize: isMobile ? '20px' : '24px'
                  }}
                >
                  Our Commitments
                </Heading>
                <Text style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  What you can expect from Murphy's support
                </Text>
              </VStack>

              <Grid columns={{ xs: 1, md: 3 }} gap="md">
                <Card padding="lg" style={{ backgroundColor: '#0F0F0F', border: 'none' }}>
                  <VStack gap="sm">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Checkmark20Regular style={{ color: '#22C55E', width: 20, height: 20 }} />
                      <Text style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 600 }}>
                        Instant AI Answers
                      </Text>
                    </HStack>
                    <Text style={{ color: '#9CA3AF', fontSize: '14px', lineHeight: 1.5 }}>
                      Murphy's AI Support responds in seconds with documentation, config examples, and step-by-step fixes.
                    </Text>
                  </VStack>
                </Card>

                <Card padding="lg" style={{ backgroundColor: '#0F0F0F', border: 'none' }}>
                  <VStack gap="sm">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Clock24Regular style={{ color: '#3B82F6', width: 20, height: 20 }} />
                      <Text style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 600 }}>
                        24h Human Response
                      </Text>
                    </HStack>
                    <Text style={{ color: '#9CA3AF', fontSize: '14px', lineHeight: 1.5 }}>
                      When AI can't solve it, our team responds within 24 hours on business days. Usually faster.
                    </Text>
                  </VStack>
                </Card>

                <Card padding="lg" style={{ backgroundColor: '#0F0F0F', border: 'none' }}>
                  <VStack gap="sm">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <PeopleCommunity24Regular style={{ color: 'var(--brand-primary)', width: 20, height: 20 }} />
                      <Text style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 600 }}>
                        Active Community
                      </Text>
                    </HStack>
                    <Text style={{ color: '#9CA3AF', fontSize: '14px', lineHeight: 1.5 }}>
                      Join 1,000+ server owners sharing tips, configs, and helping each other succeed.
                    </Text>
                  </VStack>
                </Card>
              </Grid>
            </VStack>
          </Animate>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: isMobile ? '40px 0' : '60px 0', backgroundColor: '#0F0F0F' }}>
        <div className="page-content">
          <Animate type="slideInUp" delay={100}>
            <VStack gap="lg" style={{ alignItems: 'center' }}>
              <VStack gap="sm" style={{ textAlign: 'center', alignItems: 'center' }}>
                <Heading
                  level={2}
                  style={{
                    color: '#FFFFFF',
                    fontSize: isMobile ? '20px' : '24px'
                  }}
                >
                  Frequently Asked Questions
                </Heading>
                <Text style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  Quick answers to common documentation questions
                </Text>
              </VStack>

              <div style={{ maxWidth: '800px', width: '100%' }}>
                <FAQAccordion items={faqItems} />
              </div>
            </VStack>
          </Animate>
        </div>
      </section>

      {/* Divider */}
      <div style={{ width: '100%', height: '1.5px', backgroundColor: '#1A1A1A' }} />

      {/* Final CTA Section */}
      <section style={{ padding: isMobile ? '40px 0' : '60px 0', backgroundColor: '#161616' }}>
        <div className="page-content">
          <Animate type="slideInUp" delay={150}>
            <HStack
              gap="lg"
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: isMobile ? 'wrap' : 'nowrap'
              }}
            >
              <VStack gap="xs" style={{ flex: 1 }}>
                <Heading
                  level={3}
                  style={{
                    color: '#FFFFFF',
                    fontSize: isMobile ? '18px' : '22px'
                  }}
                >
                  Still Have Questions?
                </Heading>
                <Text style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  Our community and team are ready to help. No question is too small.
                </Text>
              </VStack>
              <HStack
                gap="xs"
                className="clickable link-hover"
                style={{ alignItems: 'center', flexShrink: 0, marginTop: isMobile ? '16px' : 0 }}
                onClick={handleOpenDiscord}
              >
                <Text style={{ color: 'var(--brand-primary)', fontWeight: 400 }}>
                  Get support on Discord
                </Text>
                <ChevronRight20Regular style={{ color: 'var(--brand-primary)', width: 16, height: 16 }} />
              </HStack>
            </HStack>
          </Animate>
        </div>
      </section>

      {/* Community Section */}
      <section style={{ backgroundColor: '#0F0F0F' }}>
        <div style={{ width: '100%', height: '1.5px', backgroundColor: '#1A1A1A' }} />
        <div className="page-content">
          {isMobile ? (
            <VStack gap="lg" style={{ padding: '40px 0' }}>
              <VStack gap="md">
                <Heading
                  level={2}
                  style={{
                    color: '#FFFFFF',
                    fontSize: '22px',
                    lineHeight: 1.2
                  }}
                >
                  Become part of an<br />
                  Active <span style={{ color: 'var(--brand-primary)' }}>Community</span>
                </Heading>
                <Text style={{ color: '#CACACA', fontSize: '14px' }}>
                  Enter one of the most active community of server owners, developers, and players.
                </Text>
                <HStack
                  gap="xs"
                  className="clickable link-hover"
                  style={{ alignItems: 'center' }}
                  onClick={handleOpenDiscord}
                >
                  <Text style={{ color: 'var(--brand-primary)', fontWeight: 400 }}>
                    Join our discord
                  </Text>
                  <ChevronRight20Regular style={{ color: 'var(--brand-primary)', width: 16, height: 16 }} />
                </HStack>
              </VStack>

              <div style={{ width: '100%', overflow: 'hidden' }}>
                <img
                  src="/visu_become_part.png"
                  alt="Murphy Discord Community"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </VStack>
          ) : (
            <HStack
              gap={isTablet ? 'lg' : 'xl'}
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap'
              }}
            >
              <VStack gap="md" style={{
                flex: 1,
                minWidth: '280px',
                maxWidth: isTablet ? '350px' : '400px',
                paddingTop: isTablet ? '60px' : '100px',
                paddingBottom: isTablet ? '60px' : '100px'
              }}>
                <Heading
                  level={2}
                  style={{
                    color: '#FFFFFF',
                    fontSize: isTablet ? '22px' : '24px',
                    lineHeight: 1.2
                  }}
                >
                  Become part of an<br />
                  Active <span style={{ color: 'var(--brand-primary)' }}>Community</span>
                </Heading>
                <Text style={{ color: '#CACACA' }}>
                  {isTablet ? (
                    'Enter one of the most active community of server owners, developers, and players.'
                  ) : (
                    <>Enter one of the most active community of server<br />owners, developers, and players.</>
                  )}
                </Text>
                <HStack
                  gap="xs"
                  className="clickable link-hover"
                  style={{ alignItems: 'center' }}
                  onClick={handleOpenDiscord}
                >
                  <Text style={{ color: 'var(--brand-primary)', fontWeight: 400 }}>
                    Join our discord
                  </Text>
                  <ChevronRight20Regular style={{ color: 'var(--brand-primary)', width: 16, height: 16 }} />
                </HStack>
              </VStack>

              <div
                style={{
                  flex: 1,
                  minWidth: '280px',
                  maxWidth: isTablet ? '450px' : '600px',
                  overflow: 'hidden'
                }}
              >
                <img
                  src="/visu_become_part.png"
                  alt="Murphy Discord Community"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </div>
            </HStack>
          )}
        </div>
        <div style={{ width: '100%', height: '1.5px', backgroundColor: '#1A1A1A' }} />
      </section>

      {/* Browse Documentation CTA Section */}
      <section style={{ padding: isMobile ? '60px 0 80px 0' : '80px 0 120px 0', backgroundColor: '#0F0F0F' }}>
        <div className="page-content">
          <Animate type="slideInUp" delay={200}>
            <VStack gap="lg" style={{ alignItems: 'center', textAlign: 'center' }}>
              <VStack gap="md" style={{ alignItems: 'center' }}>
                <Heading
                  level={2}
                  style={{
                    color: '#FFFFFF',
                    fontSize: isMobile ? '28px' : isTablet ? '32px' : '40px',
                    lineHeight: 1.2
                  }}
                >
                  Ready to explore our<br />
                  <span style={{ color: 'var(--brand-primary)' }}>Documentation</span>?
                </Heading>
                <Text style={{ color: '#CACACA', fontSize: isMobile ? '14px' : '16px', maxWidth: '500px' }}>
                  Browse comprehensive guides, configuration references, and examples for all Murphy Workshop plugins.
                </Text>
              </VStack>

              <HStack gap="md" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  icon={<Book24Regular />}
                  onClick={() => window.location.href = '/'}
                  style={{ background: 'linear-gradient(155deg, #f6a00e 0%, #8B5A00 100%)' }}
                >
                  Browse Documentation
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleOpenShop}
                >
                  Visit Shop
                </Button>
              </HStack>
            </VStack>
          </Animate>
        </div>
      </section>
    </VStack>
  )
}
